import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Link2 } from 'lucide-react'
import { AppCtaLink, GradientText } from './page-shell'

/** Link input CTA - decorative input + button */
export function LinkInputCta({ buttonLabel = '一键开始' }: { buttonLabel?: string }) {
  return (
    <div className="mt-8 flex flex-col items-center gap-4">
      <div className="flex w-full max-w-lg items-center rounded-full border border-border bg-card px-4 py-3 shadow-sm">
        <Link2 className="size-5 text-muted-foreground" />
        <span className="ml-3 flex-1 text-sm text-muted-foreground">
          粘贴视频链接或上传素材开始...
        </span>
      </div>
      <AppCtaLink className="px-8 py-3">
        {buttonLabel}
      </AppCtaLink>
    </div>
  )
}

/** Pricing CTA section */
export function PricingCta() {
  return (
    <section className="mx-auto mt-24 max-w-4xl px-4 sm:px-6">
      <div className="rounded-3xl border border-border bg-card p-8 text-center sm:p-12">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          准备开始了吗？
        </p>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          今天就开始使用 JIAN
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm text-muted-foreground">
          AI 生成初稿，你在时间线里精修成片--从素材到导出，一个项目完成。
        </p>
        <AppCtaLink className="mt-8 px-8 py-3">
          立即体验 JIAN
        </AppCtaLink>
      </div>
    </section>
  )
}

/** Hero badges row */
export function HeroBadges({ items }: { items: string[] }) {
  return (
    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
      {items.map((item) => (
        <span
          key={item}
          className="rounded-full border border-border bg-card px-4 py-1.5 text-xs font-medium text-muted-foreground"
        >
          {item}
        </span>
      ))}
    </div>
  )
}

/** Simple hero section */
export function SimpleHero({
  eyebrow,
  title,
  highlighted,
  description,
  buttonLabel,
  secondaryLabel,
  secondaryHref,
}: {
  eyebrow: string
  title: string
  highlighted: string
  description: string
  buttonLabel: string
  secondaryLabel?: string
  secondaryHref?: string
}) {
  return (
    <section className="mx-auto max-w-3xl px-4 pt-20 text-center sm:px-6">
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">{eyebrow}</p>
      <h1 className="mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
        {title} <GradientText>{highlighted}</GradientText>
      </h1>
      <p className="mx-auto mt-6 max-w-xl text-base text-muted-foreground">{description}</p>

      <div className="mt-8 flex flex-col items-center gap-4">
        {secondaryLabel ? (
          <div className="flex items-center gap-4">
            <AppCtaLink className="px-6 py-3">
              {buttonLabel}
            </AppCtaLink>
            <Link
              to={secondaryHref ?? '/docs'}
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            >
              {secondaryLabel}
            </Link>
          </div>
        ) : (
          <LinkInputCta buttonLabel={buttonLabel} />
        )}
      </div>
    </section>
  )
}

/** Pill badge */
export function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
      {children}
    </span>
  )
}

/** Panel card */
export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={
        'rounded-2xl border border-border bg-card p-6 sm:p-8 ' + (className ?? '')
      }
    >
      {children}
    </div>
  )
}

/** 演示视频数据（数字人生成效果） */
const DEMO_VIDEOS = [
  { src: '/video/jingqu.mp4', title: '景区介绍', description: '数字人讲解景区' },
  { src: '/video/maihuo.mp4', title: '直播带货', description: '数字人带货口播' },
  { src: '/video/yiyuan.mp4', title: '医院导诊', description: '数字人导诊服务' },
] as const

/** 演示视频展示区块 */
export function DemoVideosSection({ title = 'AI 创作效果演示' }: { title?: string }) {
  return (
    <section className="pb-22">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
        <p className="text-sm font-medium uppercase tracking-[0.18em] text-primary">
          效果演示
        </p>
        <h2 className="mt-5 text-balance text-[36px] font-semibold leading-[1.04] tracking-[-0.035em] text-foreground md:text-[54px]">
          {title}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-7 text-muted-foreground">
          以下视频由 JIAN 的数字人生成能力产出，可直接进入时间线继续编辑。
        </p>
      </div>

      <div className="mx-auto mt-12 grid max-w-5xl gap-5 px-4 sm:px-6 md:grid-cols-3">
        {DEMO_VIDEOS.map((video) => (
          <div key={video.src} className="overflow-hidden rounded-2xl border border-border bg-card">
            <video
              src={video.src}
              controls
              preload="metadata"
              className="aspect-video w-full bg-black"
            />
            <div className="px-4 py-3">
              <div className="text-sm font-semibold text-foreground">{video.title}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{video.description}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
